import { useOnClickOutside, useResizeObserver } from '@passkeys/hooks';
import Text from '@passkeys/ui/text';
import { useCallback, useRef, useState } from 'react';

import type { Asset } from '@/types';
import { cn } from '@/ui/utils/classnames';

type TitleProps = {
  asset?: Asset;
};

function Title({ asset }: TitleProps) {
  const [truncated, setTruncated] = useState(false);
  const [touchTooltipVisible, setTouchTooltipVisible] = useState(false);
  const elementRef = useRef<HTMLHeadingElement>(null);

  const displayName = asset?.displayName ?? '';
  const small = displayName.length > 35;

  const detectTruncation = useCallback(() => {
    if (!elementRef.current) {
      return;
    }

    const { offsetWidth, scrollWidth } = elementRef.current;
    setTruncated(offsetWidth < scrollWidth);
  }, []);

  useResizeObserver({
    ref: elementRef,
    onResize: detectTruncation,
  });

  const showTouchTooltip = useCallback(() => {
    setTouchTooltipVisible(true);
  }, []);

  const hideTouchTooltip = useCallback(() => {
    setTouchTooltipVisible(false);
  }, []);

  useOnClickOutside({ ref: elementRef, handler: hideTouchTooltip });

  return (
    <div className="relative min-w-0 px-6" onClick={showTouchTooltip}>
      <Text
        ref={elementRef}
        as="h1"
        className={cn('peer truncate', small && 'text-xs')}
      >
        {displayName}
      </Text>

      {truncated && (
        <div
          className={cn(
            'absolute -bottom-4 left-1/2 z-50 hidden w-max max-w-full -translate-x-1/2 translate-y-full rounded bg-[#ebebeb] px-2 py-1 text-xs text-gray-600 peer-hover:block',
            'theme-dark:bg-[#292929] theme-dark:text-neutral-100',
            touchTooltipVisible && '[@media(hover:none)]:block',
          )}
        >
          {displayName}
        </div>
      )}
    </div>
  );
}

export default Title;
